import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from 'lucide-react';

import { getBreadcrumbSchema, getWebPageSchema, getPersonSchema, getAboutPageSchema, getArticleSchema, getFAQPageSchema, extractFAQsFromHTML, combineSchemas } from '../utils/structuredData';
import { isPrerendering } from '../services/api';
import { getStaticData } from '../hooks/useStaticData';

import SEO from './SEO';
import Breadcrumb from './Breadcrumb';

const CustomPage = () => {
  const { slug } = useParams();
  // Try to get page from static data first
  const staticPages = getStaticData('pages') || [];
  const staticPage = staticPages.find(p => p.slug === slug);

  const [page, setPage] = useState(staticPage || null);
  const [loading, setLoading] = useState(!staticPage && !isPrerendering());
  const [error, setError] = useState(null);

  useEffect(() => {
    // During pre-rendering, use static data if available
    if (isPrerendering()) {
      if (staticPage) {
        setPage(staticPage);
        setLoading(false);
      }
      return;
    }

    // If we have static data, use it (but still fetch to ensure freshness in browser)
    if (staticPage) {
      setPage(staticPage);
    }

    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pages/${slug}`);
      
      if (response.ok) {
        const data = await response.json();
        setPage(data);
      } else if (response.status === 404) {
        setError('Page not found');
      } else {
        setError('Failed to load page');
      }
    } catch (error) {
      console.error('Error loading page:', error);
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-red-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-xl text-gray-600">{error}</p>
          <a href="/" className="mt-6 inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors">
            Go Home
          </a>
        </div>
      </div>
    );
  }

  if (!page) return null;

  // Determine SEO description priority: meta_description > subtitle > default
  const seoDescription = page.meta_description
    || page.subtitle
    || `Learn more about ${page.title} at Owaza Martial Arts.`;

  // Generate breadcrumb schema
  const breadcrumbs = getBreadcrumbSchema([
    { name: "Home", path: "/" },
    { name: page.title, path: `/page/${slug}` }
  ]);

  // Determine page type and generate appropriate schema
  const bioPageSlugs = ['sijo-jason-velez', 'sensei-steven-reyes', 'dr-juan-otero-jr-hanshi'];
  const isBioPage = bioPageSlugs.includes(slug) || page.page_type === 'bio';
  const isAboutPage = slug === 'who-we-are' || page.page_type === 'about';
  const isArticle = page.page_type === 'article';

  let pageSchema;
  if (isBioPage) {
    // Use Person schema for instructor bio pages
    pageSchema = getPersonSchema({
      name: page.title,
      description: seoDescription,
      image: page.hero_image_url,
      jobTitle: page.subtitle,
      url: `/page/${slug}`,
      knowsAbout: ['Martial Arts', 'Jiu-Jitsu', 'Self-Defense']
    });
  } else if (isAboutPage) {
    // Use AboutPage schema for about/team pages
    pageSchema = getAboutPageSchema({
      title: page.title,
      description: seoDescription,
      url: `/page/${slug}`
    });
  } else if (isArticle) {
    // Use Article schema for blog/news pages
    pageSchema = getArticleSchema({
      title: page.title,
      description: seoDescription,
      url: `/page/${slug}`,
      image: page.hero_image_url,
      datePublished: page.created_at,
      dateModified: page.updated_at
    });
  } else {
    // Default WebPage schema
    pageSchema = getWebPageSchema({
      title: page.title,
      description: seoDescription,
      url: `/page/${slug}`
    });
  }

  // Check for FAQs in content and add FAQ schema if detected
  const detectedFAQs = extractFAQsFromHTML(page.content);
  let finalSchema;

  if (detectedFAQs.length >= 2) {
    // If 2+ FAQs detected, add FAQ schema to the mix
    const faqSchema = getFAQPageSchema({
      title: page.title,
      url: `/page/${slug}`,
      faqs: detectedFAQs
    });
    finalSchema = combineSchemas(breadcrumbs, pageSchema, faqSchema);
  } else {
    finalSchema = combineSchemas(breadcrumbs, pageSchema);
  }

  return (
    <div className="min-h-screen">
      <SEO
        title={page.og_title || page.title}
        description={page.og_description || seoDescription}
        canonical={page.canonical_url || `/page/${slug}`}
        ogImage={page.og_image_url || page.hero_image_url}
        jsonLd={finalSchema}
        keywords={page.keywords}
        twitterCard={page.twitter_card_type}
        noindex={page.no_index}
        ogTitle={page.og_title}
        ogDescription={page.og_description}
      />

      {/* Breadcrumb Navigation */}
      <Breadcrumb items={[
        { name: "Home", path: "/" },
        { name: page.title, path: `/page/${slug}` }
      ]} />

      {/* Hero Section */}
      <section className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '280px' }}>
        {/* Background Image */}
        {page.hero_image_url && (
          <div className="absolute inset-0">
            <img
              src={page.hero_image_url}
              alt={page.title}
              className="w-full h-full object-cover"
              fetchpriority="high"
              loading="eager"
            />
            <div className="absolute inset-0 bg-black/60"></div>
          </div>
        )}

        {/* No Image - Gradient Background with Pattern */}
        {!page.hero_image_url && (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-red-800"></div>
            {/* Subtle decorative pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-1/4 left-1/4 w-64 h-64 border border-white/30 rounded-full"></div>
              <div className="absolute bottom-1/4 right-1/4 w-48 h-48 border border-white/20 rounded-full"></div>
              <div className="absolute top-1/2 right-1/3 w-32 h-32 border border-white/20 rounded-full"></div>
            </div>
          </>
        )}

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          {/* Decorative line above title */}
          <div className="flex items-center justify-center mb-6">
            <div className="h-px w-12 bg-white/40"></div>
            <div className="mx-4 w-2 h-2 bg-white/60 rotate-45"></div>
            <div className="h-px w-12 bg-white/40"></div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
            {page.title}
          </h1>

          {page.subtitle && (
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto font-light">
              {page.subtitle}
            </p>
          )}

          {/* Decorative line below */}
          <div className="flex items-center justify-center mt-6">
            <div className="h-px w-16 bg-white/30"></div>
          </div>
        </div>

        {/* Bottom curve transition */}
        <div className="absolute -bottom-px left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block">
            <path d="M0 60V30C240 10 480 0 720 0C960 0 1200 10 1440 30V60H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Page Content */}
      <section className="pt-6 md:pt-8 pb-16 md:pb-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="custom-page-content"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>
        
        {/* Custom Styles for Page Content */}
        <style jsx>{`
          .custom-page-content {
            font-size: 1.125rem;
            line-height: 1.75;
            color: #374151;
          }
          
          .custom-page-content p {
            margin: 1.25rem 0;
            line-height: 1.75;
          }
          
          .custom-page-content p:first-child {
            margin-top: 0;
          }
          
          .custom-page-content p:last-child {
            margin-bottom: 0;
          }
          
          .custom-page-content h1 {
            font-size: 2.25rem;
            font-weight: bold;
            color: #111827;
            margin: 2rem 0 1rem 0;
            line-height: 1.2;
          }
          
          .custom-page-content h2 {
            font-size: 1.875rem;
            font-weight: bold;
            color: #111827;
            margin: 1.75rem 0 1rem 0;
            line-height: 1.3;
          }
          
          .custom-page-content h3 {
            font-size: 1.5rem;
            font-weight: 600;
            color: #111827;
            margin: 1.5rem 0 0.75rem 0;
            line-height: 1.4;
          }
          
          .custom-page-content ul, 
          .custom-page-content ol {
            margin: 1.25rem 0;
            padding-left: 2rem;
          }
          
          .custom-page-content li {
            margin: 0.5rem 0;
            line-height: 1.75;
          }
          
          .custom-page-content ul {
            list-style-type: disc;
          }
          
          .custom-page-content ol {
            list-style-type: decimal;
          }
          
          .custom-page-content a {
            color: #dc2626;
            text-decoration: underline;
          }
          
          .custom-page-content a:hover {
            color: #b91c1c;
          }
          
          .custom-page-content strong {
            font-weight: 600;
            color: #111827;
          }
          
          .custom-page-content em {
            font-style: italic;
          }
          
          .custom-page-content img {
            max-width: 100%;
            height: auto;
            border-radius: 0.5rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            margin: 2rem auto;
            display: block;
          }
          
          .custom-page-content blockquote {
            border-left: 4px solid #dc2626;
            padding-left: 1.5rem;
            margin: 1.5rem 0;
            font-style: italic;
            color: #6b7280;
          }
          
          .custom-page-content code {
            background-color: #f3f4f6;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            font-size: 0.875em;
            font-family: monospace;
          }
        `}</style>
      </section>
    </div>
  );
};

export default CustomPage;
